import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SkipForward, StopCircle } from "lucide-react";
import { toast } from "sonner";
import SectionHeader from "../components/common/SectionHeader";
import OpportunityDetail from "../components/opportunity/OpportunityDetail";
import EmptyState from "../components/common/EmptyState";
import { useOpportunities } from "../hooks/data";
import { getEngine } from "../config/engines";
import { fmtMoney } from "../lib/format";
import { T } from "../lib/testids";

/**
 * "Trabajo de hoy" — un cliente a la vez, ordenado por prioridad real
 * (Opportunity.priority, luego valor estimado). Reemplaza el "Modo turno"
 * con timer/duración: la entrada desde "Hoy" debe llevar aquí de
 * inmediato, sin una pantalla de configuración intermedia (Sprint 11D).
 *
 * Filtra por categoría cuando llega desde el menú de "Hoy"
 * (location.state.engineId); sin eso, muestra todo lo pendiente.
 */
const PRIORITY_RANK = { high: 0, medium: 1, low: 2 };
const TERMINAL_OUTCOMES = ["won", "lost", "no_response"];

export default function Turn() {
  const location = useLocation();
  const navigate = useNavigate();
  const engineId = location.state?.engineId;
  const engine = engineId ? getEngine(engineId) : null;
  const categoryLabel = engine?.humanLabel || "tu cartera";

  const { data: opps, loading } = useOpportunities(
    engineId
      ? { engineId, outcome: "pending", limit: 50 }
      : { outcome: "pending", limit: 50 }
  );

  const [queueIds, setQueueIds] = useState(null); // null = todavía no armada
  const [currentIdx, setCurrentIdx] = useState(0);
  const [finished, setFinished] = useState(false);
  const [sessionOutcomes, setSessionOutcomes] = useState([]);

  useEffect(() => {
    if (!loading && queueIds === null) {
      const sorted = [...opps].sort((a, b) => {
        const rankDiff = (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9);
        if (rankDiff !== 0) return rankDiff;
        return b.estimatedValue.value.amount - a.estimatedValue.value.amount;
      });
      setQueueIds(sorted.map((o) => o.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const inPlay = useMemo(() => {
    if (!queueIds) return 0;
    const set = new Set(queueIds);
    return opps
      .filter((o) => set.has(o.id))
      .reduce((s, o) => s + o.estimatedValue.value.amount, 0);
  }, [opps, queueIds]);

  const recovered = sessionOutcomes
    .filter((o) => o.result === "won")
    .reduce((s, o) => s + o.amount, 0);

  const next = () => {
    if (queueIds && currentIdx < queueIds.length - 1) {
      setCurrentIdx((i) => i + 1);
    } else {
      setFinished(true);
    }
  };

  const handleUpdated = (updatedOpp) => {
    setSessionOutcomes((prev) => [
      ...prev,
      {
        id: updatedOpp.id,
        result: updatedOpp.outcome,
        amount: updatedOpp.estimatedValue.value.amount,
      },
    ]);
    if (TERMINAL_OUTCOMES.includes(updatedOpp.outcome)) {
      toast.success("Listo. Vamos con el siguiente...");
      setTimeout(next, 900);
    }
  };

  if (loading || queueIds === null) {
    return (
      <div className="ambient-bg min-h-full">
        <EmptyState title="Preparando tu trabajo de hoy..." />
      </div>
    );
  }

  if (queueIds.length === 0) {
    return (
      <div className="ambient-bg min-h-full px-4 sm:px-8 py-8 sm:py-10">
        <EmptyState
          title={`No hay pendientes en ${categoryLabel.toLowerCase()} por ahora`}
          description="Buen momento para revisar otra categoría o tu cartera completa."
          action={
            <button
              onClick={() => navigate("/")}
              className="text-[11px] font-mono uppercase tracking-[0.15em] px-4 py-2 border border-amber-500 text-amber-400 hover:bg-amber-500/10"
            >
              Volver a Hoy
            </button>
          }
        />
      </div>
    );
  }

  if (finished) {
    return (
      <div className="ambient-bg min-h-full px-4 sm:px-8 py-8 sm:py-10" data-testid={T.turnSummary}>
        <SectionHeader overline="Trabajo de hoy" title="Buen trabajo" />
        <p className="text-sm text-zinc-400 mt-2 max-w-xl">
          Atendiste a {sessionOutcomes.length} de {queueIds.length} clientes en{" "}
          {categoryLabel.toLowerCase()}.
        </p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
          <div className="border border-[#17223a] bg-[#0d1321] p-5">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
              Clientes atendidos
            </div>
            <div className="font-display font-black text-3xl tabular-nums text-zinc-100 mt-2">
              {sessionOutcomes.length}
            </div>
          </div>
          <div className="border border-[#17223a] bg-[#0d1321] p-5">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
              Dinero recuperado hoy
            </div>
            <div className="font-display font-black text-3xl tabular-nums text-emerald-400 mt-2">
              {fmtMoney(recovered)}
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate("/")}
          className="mt-8 text-[11px] font-mono uppercase tracking-[0.2em] px-5 py-2.5 border border-[#1b2846] text-zinc-300 hover:border-amber-500/50 hover:text-amber-400 transition-colors"
        >
          Volver a Hoy
        </button>
      </div>
    );
  }

  const currentId = queueIds[currentIdx];
  return (
    <div className="ambient-bg min-h-full flex flex-col">
      <div className="border-b border-[#17223a] bg-[#0d1321] px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sticky top-0 z-20">
        <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
          <div>
            <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">
              Trabajo de hoy
            </div>
            <div className="font-display font-bold text-sm text-zinc-100">{categoryLabel}</div>
          </div>
          <div className="border-l border-[#17223a] pl-4 sm:pl-6">
            <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">
              Cliente
            </div>
            <div
              data-testid={T.turnActiveTimer}
              className="font-display font-black text-xl sm:text-2xl tabular-nums text-zinc-100"
            >
              {currentIdx + 1}/{queueIds.length}
            </div>
          </div>
          <div className="border-l border-[#17223a] pl-4 sm:pl-6">
            <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">
              Potencial de ingreso
            </div>
            <div className="font-display font-black text-xl sm:text-2xl tabular-nums text-amber-400">
              {/* Mismo criterio que AuditableFigure (Regla #3): "USD 0" no
                  comunica nada útil cuando sí hay clientes en cola pero sus
                  valores todavía no se calculan. */}
              {queueIds.length > 0 && inPlay === 0
                ? <span className="text-sm sm:text-base italic text-zinc-500">Calculando</span>
                : fmtMoney(inPlay)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={T.turnNext}
            onClick={next}
            className="flex-1 sm:flex-none justify-center text-[11px] font-mono uppercase tracking-[0.2em] px-4 py-2.5 sm:py-2 border border-amber-500 text-amber-400 hover:bg-amber-500/10 flex items-center gap-2"
          >
            <SkipForward size={12} /> Siguiente
          </button>
          <button
            data-testid={T.turnEnd}
            onClick={() => setFinished(true)}
            className="flex-1 sm:flex-none justify-center text-[11px] font-mono uppercase tracking-[0.2em] px-4 py-2.5 sm:py-2 border border-[#1b2846] text-zinc-400 hover:border-red-500/50 hover:text-red-400 flex items-center gap-2"
          >
            <StopCircle size={12} /> Terminar
          </button>
        </div>
      </div>
      <div className="flex-1" data-testid={T.turnActiveCard}>
        <OpportunityDetail opportunityId={currentId} onUpdated={handleUpdated} />
      </div>
    </div>
  );
}
